import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Edit3,
  ArrowRightLeft,
  Image as ImageIcon,
  Package,
  Boxes,
  AlertTriangle,
  PieChart,
  ArrowUpSquare,
  RefreshCw,
  Edit2,
  Pencil,
  Loader
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { masterService } from "../api/masterService";
import AddStockLocationModal from "../components/master/AddStockLocationModal";
import SearchableSelect from "../components/SearchableSelect";
import galleryIcon from "../assets/img/gallery.png";
import { getImageUrl } from "../utils/imageUtils";

export default function StockLocationDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [activeTab, setActiveTab] = useState("Produk di Lokasi");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [detailData, setDetailData] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const res = await masterService.stockLocations.getDetail(id);
      if (res && res.data) {
        setDetailData(res.data);
      } else if (res) {
        setDetailData(res);
      }
    } catch (err) {
      console.error("Error fetching stock location detail:", err);
    } finally {
      setLoading(false);
    }
  };

  const sidebarToggle = () => {
    const newState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newState);
    localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "-";
      return new Intl.DateTimeFormat("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(d);
    } catch {
      return "-";
    }
  };

  const loc = detailData?.location || {};
  const products = detailData?.products || [];
  const categorySummary = detailData?.category_summary || [];
  const activities = detailData?.activities || [];
  const activeItems = activeTab === "Produk di Lokasi" ? products : categorySummary;
  const maxPages = Math.max(1, Math.ceil(activeItems.length / itemsPerPage));
  const pageItems = activeItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const startEntry = activeItems.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endEntry = Math.min(currentPage * itemsPerPage, activeItems.length);

  const changeTab = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const renderPagination = (label) => (
    <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/40 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <span className="text-sm text-slate-500">
          Showing <span className="font-medium text-slate-700">{startEntry}</span> to <span className="font-medium text-slate-700">{endEntry}</span> of <span className="font-medium text-slate-700">{activeItems.length}</span> {label}
        </span>
        <SearchableSelect
          options={[{ value: 10, label: "10" }, { value: 25, label: "25" }, { value: 50, label: "50" }, { value: 100, label: "100" }]}
          value={itemsPerPage}
          onChange={(value) => { setItemsPerPage(Number(value)); setCurrentPage(1); }}
          placeholder="Items per page"
          displayKey="label"
          valueKey="value"
          className="w-20"
          searchable={false}
          size="sm"
        />
      </div>
      <div className="flex items-center gap-1.5">
        <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">‹‹</button>
        <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">‹</button>
        {Array.from({ length: Math.min(3, maxPages) }, (_, i) => {
          let page = currentPage === 1 ? 1 : currentPage === maxPages ? maxPages - 2 : currentPage - 1;
          if (maxPages < 3) page = 1;
          const pageNum = page + i;
          if (pageNum > maxPages || pageNum < 1) return null;
          return <button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum ? "bg-teal-600 text-white shadow-sm shadow-teal-600/20" : "bg-white ring-1 ring-slate-200 text-slate-700 hover:bg-slate-100"}`}>{pageNum}</button>;
        })}
        <button onClick={() => setCurrentPage(Math.min(maxPages, currentPage + 1))} disabled={currentPage === maxPages} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">›</button>
        <button onClick={() => setCurrentPage(maxPages)} disabled={currentPage === maxPages} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white ring-1 ring-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">››</button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#f8fafc]">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggle={sidebarToggle} activePage="master-data" />
      <div className={`flex-1 overflow-auto transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
        <Header />
        
        {loading ? (
          <div className="p-12 flex flex-col items-center justify-center text-slate-500 min-h-[60vh]">
            <Loader className="w-8 h-8 animate-spin text-teal-600 mb-3" />
            <p className="font-semibold text-sm">Memuat detail lokasi stok...</p>
          </div>
        ) : (
          <div className="p-6 space-y-5 pb-16">
            
            {/* Top Bar */}
            <div>
              <div className="flex items-center text-sm font-medium text-slate-500 mb-2">
                <span className="text-teal-600 hover:underline cursor-pointer" onClick={() => navigate("/master-data/lokasi-stok")}>Lokasi Stok</span>
                <span className="mx-2 text-slate-300">/</span>
                <span className="text-slate-700">Detail Lokasi</span>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                   <button onClick={() => navigate("/master-data/lokasi-stok")} className="w-9 h-9 flex items-center justify-center bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition shadow-sm" title="Kembali ke daftar lokasi">
                     <ChevronLeft className="w-5 h-5" />
                   </button>
                   <h1 className="text-2xl font-bold text-gray-900">Detail Lokasi</h1>
                  </div>
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => setIsEditModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 font-semibold text-sm transition shadow-sm cursor-pointer"
                  >
                    <Edit3 className="w-4 h-4" /> Edit Lokasi
                  </button>
                  <button 
                    onClick={() => navigate("/inventori/konversi/stok")}
                    className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 border border-teal-600 text-white rounded-xl hover:bg-teal-700 font-semibold text-sm transition shadow-sm cursor-pointer"
                  >
                    <ArrowRightLeft className="w-4 h-4" /> Transfer Stok
                  </button>
                </div>
              </div>
            </div>

            {/* Banner Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden flex flex-col md:flex-row p-6 gap-8 items-stretch">
              <div className="w-full md:w-[400px] aspect-[16/9] rounded-xl overflow-hidden relative group shrink-0 bg-slate-100 border border-slate-200">
                {loc.image_url && loc.image_url.trim() !== "" ? (
                  <img 
                    src={loc.image_url} 
                    alt={loc.name} 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 bg-slate-50">
                    <ImageIcon className="w-10 h-10 mb-1 text-slate-300" />
                    <span className="text-xs font-medium">Belum ada foto lokasi</span>
                  </div>
                )}
                {loc.image_url && loc.image_url.trim() !== "" && (
                  <button 
                    onClick={() => window.open(loc.image_url, "_blank")}
                    className="absolute bottom-3 left-3 flex items-center gap-2 bg-white/90 backdrop-blur text-slate-700 px-3 py-1.5 rounded-lg text-sm font-semibold shadow-sm hover:bg-white transition cursor-pointer"
                  >
                    <ImageIcon className="w-4 h-4" /> Lihat Foto
                  </button>
                )}
              </div>
              
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <div className="space-y-6">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 mb-1">Nama Lokasi</p>
                    <div className="flex items-center gap-3">
                      <h2 className="text-[20px] font-bold text-slate-900">{loc.name || "-"}</h2>
                      <span className={`px-2.5 py-0.5 text-xs font-bold rounded border ${loc.is_active !== false ? "bg-emerald-50 text-emerald-600 border-emerald-100" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                        {loc.is_active !== false ? "Aktif" : "Non-Aktif"}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 mb-1">Tipe</p>
                    <p className="text-[15px] font-medium text-slate-800">{loc.type || "-"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 mb-1">Kode Lokasi</p>
                    <p className="text-[15px] font-medium text-slate-800">{loc.code || "-"}</p>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 mb-1">Terakhir Update</p>
                    <p className="text-[15px] font-medium text-slate-800">{formatDate(loc.updated_at || loc.updatedAt)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 mb-1">Kapasitas</p>
                    <p className="text-[15px] font-medium text-slate-800">
                      {(detailData?.capacity || loc.capacity || 0) > 0
                        ? `${(detailData?.capacity || loc.capacity).toLocaleString('id-ID')} unit`
                        : "Belum diatur"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 mb-1">Deskripsi</p>
                    <p className="text-[14px] leading-relaxed text-slate-600 pr-4">{loc.description && loc.description.trim() !== "" ? loc.description : "-"}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center shrink-0">
                  <Package className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Total Produk</p>
                  <div className="flex items-baseline gap-1.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                    <h3 className="text-2xl font-black text-slate-800">{(detailData?.total_products || 0).toLocaleString('id-ID')}</h3>
                    <span className="text-sm font-medium text-slate-400 font-sans">SKU</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                  <Boxes className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Total Stok</p>
                  <div className="flex items-baseline gap-1.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                    <h3 className="text-2xl font-black text-slate-800">{(detailData?.total_stock || 0).toLocaleString('id-ID')}</h3>
                    <span className="text-sm font-medium text-slate-400 font-sans">unit</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Stok Minimum</p>
                  <div className="flex items-baseline gap-1.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                    <h3 className="text-2xl font-black text-slate-800">{(detailData?.min_stock_sku || 0).toLocaleString('id-ID')}</h3>
                    <span className="text-sm font-medium text-slate-400 font-sans">SKU</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center shrink-0">
                  <PieChart className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Kapasitas Terpakai</p>
                  {(detailData?.capacity || loc.capacity || 0) > 0 ? (
                    <>
                      <div className="flex items-baseline gap-1.5 mb-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                        <h3 className="text-2xl font-black text-slate-800">{Math.round(detailData?.capacity_used_pct || 0)}%</h3>
                      </div>
                      <p className="text-[11px] font-medium text-slate-500">{(detailData?.total_stock || 0).toLocaleString('id-ID')} / {(detailData?.capacity || loc.capacity).toLocaleString('id-ID')} unit</p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1.5 mb-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                        <h3 className="text-lg font-bold text-slate-400">Belum diatur</h3>
                      </div>
                      <p className="text-[11px] font-medium text-slate-500">{(detailData?.total_stock || 0).toLocaleString('id-ID')} unit tersimpan</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Two Columns Layout */}
            <div className="flex flex-col lg:flex-row gap-5">
              {/* Left Column - Tabs & Tables */}
              <div className="flex-1 bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden flex flex-col">
                {/* Tabs */}
                <div className="flex items-center gap-6 px-6 border-b border-slate-100">
                  <button
                    className={`py-4 text-[13px] font-bold uppercase tracking-wider transition-colors border-b-[3px] ${
                      activeTab === "Produk di Lokasi" 
                      ? "border-teal-500 text-teal-600" 
                      : "border-transparent text-slate-400 hover:text-slate-600"
                    }`}
                    onClick={() => changeTab("Produk di Lokasi")}
                  >
                    Produk di Lokasi
                  </button>
                  <button
                    className={`py-4 text-[13px] font-bold uppercase tracking-wider transition-colors border-b-[3px] ${
                      activeTab === "Ringkasan Kategori" 
                      ? "border-teal-500 text-teal-600" 
                      : "border-transparent text-slate-400 hover:text-slate-600"
                    }`}
                    onClick={() => changeTab("Ringkasan Kategori")}
                  >
                    Ringkasan Kategori
                  </button>
                </div>

                {/* Tab Content */}
                <div className="flex-1">
                  {activeTab === "Produk di Lokasi" && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead>
                          <tr className="bg-slate-50/50 border-b border-slate-100">
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Produk</th>
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">SKU</th>
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Kategori</th>
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">Stok</th>
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Satuan</th>
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">Minimum Stok</th>
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Terakhir Update</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-[13px]">
                          {products.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="px-6 py-12 text-center text-slate-400 font-medium">
                                Tidak ada produk tersimpan di lokasi stok ini
                              </td>
                            </tr>
                          ) : (
                            pageItems.map((item, idx) => (
                              <tr key={idx} className="hover:bg-slate-50/50 transition">
                                <td className="px-6 py-3 font-semibold text-slate-800 flex items-center gap-3">
                                  <div className="w-10 h-8 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0 overflow-hidden">
                                    {item.image_url ? <img src={getImageUrl(item.image_url)} alt={item.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = galleryIcon; e.currentTarget.className = "w-5 h-5 object-contain opacity-60"; }} /> : <img src={galleryIcon} alt="" className="w-5 h-5 object-contain opacity-60" />}
                                  </div>
                                  <div>
                                    <span>{item.name}</span>
                                    {item.has_variant && (
                                      <span className="block text-[10px] text-teal-600 font-normal">Varian: {item.variant_name}</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-3 text-slate-600">{item.sku || "-"}</td>
                                <td className="px-6 py-3 text-slate-600">{item.category}</td>
                                <td className="px-6 py-3 text-center font-bold text-slate-800">{item.stock}</td>
                                <td className="px-6 py-3 text-slate-500">{item.unit}</td>
                                <td className="px-6 py-3 text-center text-slate-600">{item.min_stock}</td>
                                <td className="px-6 py-3 text-slate-500">{formatDate(item.updated_at)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                      
                      {/* Pagination */}
                      {renderPagination("entries")}
                    </div>
                  )}

                  {activeTab === "Ringkasan Kategori" && (
                    <div className="overflow-x-auto p-2">
                      <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead>
                          <tr className="border-b border-slate-100">
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Kategori</th>
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">Jumlah Produk</th>
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">Total Stok</th>
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider text-center">Stok Minimum /<br/>Produk Hampir Habis</th>
                            <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Persentase dari<br/>total stok</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-[13px]">
                          {categorySummary.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="px-6 py-12 text-center text-slate-400 font-medium">
                                Belum ada ringkasan kategori
                              </td>
                            </tr>
                          ) : (
                            pageItems.map((item, idx) => (
                              <tr key={idx} className="hover:bg-slate-50/50 transition">
                                <td className="px-6 py-4 font-semibold text-slate-800">
                                  {item.category_name}
                                </td>
                                <td className="px-6 py-4 text-center text-slate-600 font-semibold">{item.product_count}</td>
                                <td className="px-6 py-4 text-center font-bold text-slate-800">{item.total_stock.toLocaleString('id-ID')} unit</td>
                                <td className="px-6 py-4 text-center text-slate-600">{item.min_stock_sku} SKU</td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-3 w-40">
                                    <span className="w-8 text-right font-semibold text-slate-600 text-xs">{Math.round(item.percentage)}%</span>
                                    <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                                      <div className="h-full bg-teal-500 rounded-full" style={{ width: `${Math.min(100, Math.round(item.percentage))}%` }}></div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                        {categorySummary.length > 0 && (
                          <tfoot>
                            <tr className="bg-teal-50/30">
                              <td className="px-6 py-4 font-bold text-teal-800 text-[13px]">Total</td>
                              <td className="px-6 py-4 text-center font-bold text-teal-600">{categorySummary.reduce((acc, curr) => acc + curr.product_count, 0).toLocaleString('id-ID')}</td>
                              <td className="px-6 py-4 text-center font-bold text-teal-600">{categorySummary.reduce((acc, curr) => acc + curr.total_stock, 0).toLocaleString('id-ID')} unit</td>
                              <td className="px-6 py-4 text-center font-bold text-teal-600">{categorySummary.reduce((acc, curr) => acc + curr.min_stock_sku, 0)} SKU</td>
                              <td className="px-6 py-4 font-bold text-teal-600"><span className="ml-2">100%</span></td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                      {renderPagination("entries")}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Riwayat Aktivitas */}
              <div className="w-full lg:w-[450px] shrink-0 bg-white rounded-2xl shadow-sm border border-slate-100 p-6 flex flex-col">
                <h3 className="text-[15px] font-bold text-slate-900 mb-6">Riwayat Aktivitas</h3>
                
                <div className="flex-1 relative pb-4">
                  <div className="space-y-8">
                    {activities.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">Belum ada riwayat aktivitas</p>
                    ) : (
                      activities.map((act, idx) => (
                        <div key={act.id || idx} className="flex items-start relative">
                          {idx < activities.length - 1 && (
                            <div className="absolute left-[3.5px] top-[20px] bottom-[-52px] w-px bg-slate-200 z-0"></div>
                          )}
                          
                          <div className="relative shrink-0 flex items-center h-10 w-6">
                            <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_0_4px_white] relative z-20"></div>
                            <div className="w-4 h-px bg-slate-200 z-0"></div>
                          </div>
                          
                          <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 border border-emerald-100 relative z-20">
                            {act.type.includes("Masuk") ? (
                              <ArrowUpSquare className="w-5 h-5 text-emerald-600" />
                            ) : act.type.includes("Dipindahkan") || act.type.includes("Penyesuaian") ? (
                              <ArrowRightLeft className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Pencil className="w-5 h-5 text-purple-600" />
                            )}
                          </div>
                          
                          <div className="flex-1 flex justify-between items-start pt-1 gap-2 ml-4">
                            <div>
                              <h4 className="font-bold text-slate-900 text-sm mb-1">{act.type}</h4>
                              <p className="text-xs leading-relaxed text-slate-600 pr-2">{act.description}</p>
                            </div>
                            <div className="text-right shrink-0">
                              <span className="text-[11px] font-medium text-slate-500 block">{formatDate(act.trx_date)}</span>
                              <span className="text-[10px] text-slate-400 font-medium block mt-1">Oleh: {act.performed_by_name}</span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="pt-2 mt-2">
                  <button 
                    onClick={() => navigate("/inventori/penerimaan")}
                    className="w-full py-2.5 rounded-xl border border-teal-600 text-teal-600 font-bold text-[13px] hover:bg-teal-50 transition shadow-sm cursor-pointer"
                  >
                    Lihat Semua Aktivitas
                  </button>
                </div>

              </div>
            </div>
            
          </div>
        )}
      </div>

      <AddStockLocationModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        editData={loc}
        onSuccess={fetchDetail}
      />
    </div>
  );
}
